<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import BulkPopover from './BulkPopover.vue';
import { etiquetaCasa, nomeDaEtiqueta } from 'dashboard/helper/nomeDaEtiqueta';

const emit = defineEmits(['close', 'assign']);

const { t } = useI18n();

const labels = useMapGetter('labels/getLabels');

const query = ref('');
const selectedLabels = ref([]);

const filteredLabels = computed(() => {
  const termo = query.value.trim();
  if (!termo) return labels.value;
  return labels.value.filter(label => etiquetaCasa(label.title, termo));
});

const handleAssign = () => {
  if (selectedLabels.value.length > 0) {
    emit('assign', selectedLabels.value);
  }
};
</script>

<template>
  <BulkPopover
    :titulo="t('BULK_ACTION.LABELS.ASSIGN_LABELS')"
    @close="emit('close')"
  >
    <template v-if="labels.length">
      <label class="advup-busca advup-busca--bloco bla-busca">
        <i class="i-lucide-search advup-busca__lupa" aria-hidden="true" />
        <input
          v-model="query"
          type="search"
          class="advup-busca__campo reset-base"
          :placeholder="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
          :aria-label="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
        />
      </label>
      <ul class="bla-lista">
        <li v-for="label in filteredLabels" :key="label.id">
          <label class="advup-menu__item bla-item">
            <input
              v-model="selectedLabels"
              type="checkbox"
              class="advup-caixa"
              :value="label.title"
            />
            <span
              class="bla-ponto"
              :style="{ backgroundColor: label.color }"
              aria-hidden="true"
            />
            <span class="bla-nome">{{ nomeDaEtiqueta(label.title) }}</span>
          </label>
        </li>
        <li v-if="!filteredLabels.length" class="advup-nota bla-nada">
          {{ t('BULK_ACTION.LABELS.NO_LABELS_FOUND') }}
        </li>
      </ul>
      <div class="bla-rodape">
        <button
          type="button"
          class="advup-btn-forte advup-btn--compacto bla-aplicar"
          :disabled="!selectedLabels.length"
          @click="handleAssign"
        >
          <i class="i-lucide-tag" aria-hidden="true" />
          {{
            selectedLabels.length
              ? t(
                  'BULK_ACTION.LABELS.ASSIGN_COUNT',
                  { count: selectedLabels.length },
                  selectedLabels.length
                )
              : t('BULK_ACTION.LABELS.ASSIGN_SELECTED_LABELS')
          }}
        </button>
      </div>
    </template>
    <p v-else class="advup-nota bla-nada">
      {{ t('BULK_ACTION.LABELS.NO_LABELS_FOUND') }}
    </p>
  </BulkPopover>
</template>

<style scoped>
.bla-busca {
  flex-shrink: 0;
  margin: 0 var(--esp-2) var(--esp-1);
}

.bla-lista {
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
}

.bla-item {
  cursor: pointer;
}

.bla-ponto {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: var(--r-redondo);
}

.bla-nome {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bla-nada {
  margin: 0;
  padding: var(--esp-3);
  text-align: center;
}

.bla-rodape {
  flex-shrink: 0;
  padding: var(--esp-2);
  border-top: 1px solid rgb(var(--slate-4));
}

.bla-aplicar {
  width: 100%;
}
</style>
